import axiosInstance from '../config/axios.js';

const API_BASE = '/shipment-crossings';

const appendScanFile = (formData, file) => {
  if (!(file instanceof File)) return;
  const isPdf =
    file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf');
  formData.append(isPdf ? 'scanned_pdf' : 'scanned_image', file, file.name);
};

const QR_UPLOAD_TIMEOUT_MS = 120000;

const shipmentCrossingService = {
  /** Resolve shipment(s) for crossing from a scanned image/PDF file. */
  resolveFromFile: async (file) => {
    const formData = new FormData();
    appendScanFile(formData, file);
    const response = await axiosInstance.post(`${API_BASE}/resolve`, formData, {
      headers: { 'Content-Type': undefined },
      timeout: QR_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },

  /** Resolve from a live QR decode (raw QR string or shipment id) or explicit id(s). */
  resolveFromData: async ({ qrData, shipmentId, shipmentIds, unitNo } = {}) => {
    const payload = {};
    // Always send raw QR when present so parcel unit (?u=N) is not dropped.
    if (qrData) payload.qr_data = qrData;
    if (shipmentIds?.length) payload.shipment_ids = shipmentIds;
    else if (shipmentId) payload.shipment_id = shipmentId;
    if (unitNo != null && unitNo !== '') payload.unit_no = unitNo;
    if (!payload.qr_data && !payload.shipment_id && !payload.shipment_ids) {
      throw new Error('No QR data to resolve');
    }

    const response = await axiosInstance.post(`${API_BASE}/resolve`, payload);
    return response.data;
  },

  /** Searchable active vehicle list for the crossing vehicle selector. */
  listVehicles: async ({ search } = {}) => {
    const response = await axiosInstance.get(`${API_BASE}/vehicles`, {
      params: search ? { search } : {},
    });
    return response.data;
  },

  /** ML sheets the current user can filter/cross. */
  listCrossableMlSheets: async () => {
    const response = await axiosInstance.get(`${API_BASE}/ml-sheets`);
    return response.data;
  },

  /** Paginated crossable-shipment list for the no-QR interactive page. */
  listCrossableShipments: async ({ page, perPage, search, mlSheetId } = {}) => {
    const params = {};
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;
    if (search) params.search = search;
    if (mlSheetId) params.ml_sheet_id = mlSheetId;
    const response = await axiosInstance.get(`${API_BASE}/shipments`, { params });
    return response.data;
  },

  /** Step 1: operational unload at crossing hub (no finance). */
  unloadForCrossing: async ({
    shipmentIds,
    remarks,
    verified_units_by_shipment,
    allow_legacy_qr,
  } = {}) => {
    const payload = { shipment_ids: shipmentIds };
    if (remarks) payload.remarks = remarks;
    if (verified_units_by_shipment) {
      payload.verified_units_by_shipment = verified_units_by_shipment;
    }
    if (allow_legacy_qr) payload.allow_legacy_qr = true;
    const response = await axiosInstance.post(`${API_BASE}/unload`, payload);
    return response.data;
  },

  /** Step 2: redispatch unloaded shipments onto a single vehicle. */
  createCrossing: async ({
    shipmentIds,
    newVehicleId,
    remarks,
    verified_units_by_shipment,
    allow_legacy_qr,
  } = {}) => {
    const payload = {
      shipment_ids: shipmentIds,
      new_vehicle_id: newVehicleId,
    };
    if (remarks) payload.remarks = remarks;
    if (verified_units_by_shipment) {
      payload.verified_units_by_shipment = verified_units_by_shipment;
    }
    if (allow_legacy_qr) payload.allow_legacy_qr = true;
    const response = await axiosInstance.post(`${API_BASE}/`, payload);
    return response.data;
  },

  /** Crossing history (role/company scoped). */
  listCrossings: async ({ page, perPage, search } = {}) => {
    const params = {};
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;
    if (search) params.search = search;
    const response = await axiosInstance.get(`${API_BASE}/`, { params });
    return response.data;
  },
};

export default shipmentCrossingService;
